import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Save, X, Download, Copy, FileText, FileType } from "lucide-react";
import { toast } from "sonner";

export default function LibraryItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [requisition, setRequisition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [newName, setNewName] = useState("");
  
  // Refs for auto-scrolling when adding new items
  const responsibilitiesRef = useRef(null);
  const skillsRef = useRef(null);
  const preferredSkillsRef = useRef(null);

  useEffect(() => {
    fetchRequisition();
  }, [id]);

  const fetchRequisition = async () => {
    try {
      const response = await apiClient.get(`/requisitions/library/${id}`);
      setRequisition(response.data);
      setNewName(response.data.name || response.data.title);
      setEditedData({
        title: response.data.title,
        description: response.data.description,
        requirements: response.data.requirements || [],
        skills: response.data.skills || [],
        preferred_skills: response.data.preferred_skills || [],
        education: response.data.education || "",
        experience_years: response.data.experience_years || "",
        salary_range: response.data.salary_range || "",
        location: response.data.location || "",
        location_benefits: response.data.location_benefits || "",
        company_brand: response.data.company_brand || "",
        travel: response.data.travel || "",
        relocation_provided: response.data.relocation_provided || "",
        us_citizenship: response.data.us_citizenship || [],
        security_clearance: response.data.security_clearance || "",
        job_type: response.data.job_type || "",
        shift_coverage: response.data.shift_coverage || "",
        benefits_offered: response.data.benefits_offered || []
      });
    } catch (error) {
      console.error("Failed to fetch requisition:", error);
      toast.error("Failed to load requisition");
      navigate("/library");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!newName.trim()) {
      toast.error("Name cannot be empty");
      return;
    }

    setSaving(true);
    try {
      // Check for duplicate name
      const allReqs = await apiClient.get("/requisitions/library");
      const duplicateName = allReqs.data.some(
        req => req.name.toLowerCase() === newName.trim().toLowerCase() && req.id !== id
      );

      if (duplicateName) {
        toast.error(
          <div>
            <strong>Duplicate Name</strong>
            <p className="text-sm mt-1">A requisition with this name already exists in your library.</p>
            <p className="text-sm mt-1">Please edit the <strong>Requisition Name</strong> field and try saving again.</p>
          </div>,
          { duration: 6000 }
        );
        setSaving(false);
        return;
      }

      // Clean up arrays
      const cleanedRequirements = editedData.requirements.filter(line => line.trim());
      const cleanedSkills = editedData.skills.filter(skill => skill.trim());
      const cleanedPreferredSkills = editedData.preferred_skills.filter(skill => skill.trim());

      await apiClient.put(`/requisitions/library/${id}`, {
        name: newName,
        title: editedData.title,
        description: editedData.description,
        requirements: cleanedRequirements,
        skills: cleanedSkills,
        preferred_skills: cleanedPreferredSkills,
        education: editedData.education,
        experience_years: editedData.experience_years,
        salary_range: editedData.salary_range,
        location: editedData.location,
        location_benefits: editedData.location_benefits,
        company_brand: editedData.company_brand,
        travel: editedData.travel,
        relocation_provided: editedData.relocation_provided,
        us_citizenship: editedData.us_citizenship,
        security_clearance: editedData.security_clearance,
        job_type: editedData.job_type,
        shift_coverage: editedData.shift_coverage,
        benefits_offered: editedData.benefits_offered
      });

      setRequisition({ ...requisition, ...editedData, name: newName });
      setEditMode(false);
      toast.success("Changes saved successfully!");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditedData({
      title: requisition.title,
      description: requisition.description,
      requirements: requisition.requirements || [],
      skills: requisition.skills || [],
      preferred_skills: requisition.preferred_skills || [],
      education: requisition.education || "",
      experience_years: requisition.experience_years || "",
      salary_range: requisition.salary_range || "",
      location: requisition.location || "",
      location_benefits: requisition.location_benefits || "",
      company_brand: requisition.company_brand || "",
      travel: requisition.travel || "",
      relocation_provided: requisition.relocation_provided || "",
      us_citizenship: requisition.us_citizenship || [],
      security_clearance: requisition.security_clearance || "",
      job_type: requisition.job_type || "",
      shift_coverage: requisition.shift_coverage || "",
      benefits_offered: requisition.benefits_offered || []
    });
    setNewName(requisition.name || requisition.title);
  };

  const handleCopyToClipboard = async () => {
    try {
      const response = await apiClient.get(`/requisitions/library/${id}/export/json`);
      await navigator.clipboard.writeText(response.data.content);
      toast.success("Copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await apiClient.get(`/requisitions/library/${id}/export/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = (requisition.name || requisition.title || 'requisition').replace(/\s+/g, '_');
      link.setAttribute('download', `${filename}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded!");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    }
  };

  const handleExportWord = async () => {
    try {
      const response = await apiClient.get(`/requisitions/library/${id}/export/word`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = (requisition.name || requisition.title || 'requisition').replace(/\s+/g, '_');
      link.setAttribute('download', `${filename}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Word document downloaded!");
    } catch (error) {
      console.error("Word export error:", error);
      toast.error("Failed to export Word document");
    }
  };

  const handleExportText = async () => {
    try {
      const response = await apiClient.get(`/requisitions/library/${id}/export/text`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = (requisition.name || requisition.title || 'requisition').replace(/\s+/g, '_');
      link.setAttribute('download', `${filename}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Text file downloaded!");
    } catch (error) {
      console.error("Text export error:", error);
      toast.error("Failed to export text file");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <p className="text-slate-600">Loading...</p>
      </div>
    );
  }

  if (!requisition) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/library")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Return to Req Library
            </Button>
          </div>

          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-slate-900">
              {requisition.name || requisition.title}
            </h1>

            {!editMode ? (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleCopyToClipboard}>
                  <Copy className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={handleExportText}>
                  <FileText className="h-4 w-4 mr-1" />
                  Text
                </Button>
                <Button size="sm" variant="outline" onClick={handleExportPDF}>
                  <Download className="h-4 w-4 mr-1" />
                  PDF
                </Button>
                <Button size="sm" variant="outline" onClick={handleExportWord}>
                  <FileType className="h-4 w-4 mr-1" />
                  Word
                </Button>
                <Button size="sm" onClick={() => setEditMode(true)}>
                  Edit Input
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  <Save className="h-4 w-4 mr-1" />
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancel}>
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="space-y-4">
          {editMode ? (
            <>
              {/* Edit Mode - Show all fields as cards */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Requisition Name</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    rows={2}
                    className="w-full"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Job Title</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={editedData.title}
                    onChange={(e) => setEditedData({...editedData, title: e.target.value})}
                    rows={2}
                    className="w-full"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Company Brand</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={editedData.company_brand || ''}
                    onChange={(e) => setEditedData({...editedData, company_brand: e.target.value})}
                    placeholder="Company Name"
                    rows={2}
                    className="w-full"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={editedData.description}
                    onChange={(e) => setEditedData({...editedData, description: e.target.value})}
                    rows={6}
                    className="w-full"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Key Responsibilities</CardTitle>
                </CardHeader>
                <CardContent>
                  <div ref={responsibilitiesRef} className="max-h-64 overflow-y-auto">
                    <ul className="list-disc list-outside space-y-2 pl-6">
                      {editedData.requirements.filter(req => req !== '**').map((req, idx) => (
                        <li key={idx} className="text-slate-700" style={{ display: 'list-item' }}>
                          <div className="flex items-start gap-2 bg-blue-50 p-2 rounded pr-8">
                            <textarea
                              value={req}
                              onChange={(e) => {
                                const newReqs = [...editedData.requirements.filter(r => r !== '**')];
                                newReqs[idx] = e.target.value;
                                setEditedData({...editedData, requirements: newReqs});
                              }}
                              className="flex-1 bg-transparent border-none outline-none focus:ring-0 p-0 resize-none break-words whitespace-pre-wrap min-h-[24px]"
                              placeholder="Enter responsibility"
                              style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                              onFocus={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                              }}
                              onInput={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                              }}
                            />
                            <button
                              onClick={() => {
                                const newReqs = [...editedData.requirements.filter(r => r !== '**')];
                                newReqs.splice(idx, 1);
                                setEditedData({...editedData, requirements: newReqs});
                              }}
                              className="text-red-500 hover:text-red-700 text-xs flex-shrink-0 mt-1"
                            >
                              ✕
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button
                    onClick={() => {
                      const newReqs = [...editedData.requirements.filter(r => r !== '**'), ''];
                      setEditedData({
                        ...editedData, 
                        requirements: newReqs
                      });
                      // Auto-scroll to show new item
                      setTimeout(() => {
                        if (responsibilitiesRef.current) {
                          responsibilitiesRef.current.scrollTop = responsibilitiesRef.current.scrollHeight;
                        }
                      }, 50);
                    }}
                    className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    + Add Responsibility
                  </button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Required Skills</CardTitle>
                </CardHeader>
                <CardContent>
                  <div ref={skillsRef} className="max-h-64 overflow-y-auto">
                    <ul className="list-disc list-outside space-y-2 pl-6">
                      {editedData.skills.filter(skill => skill !== '**').map((skill, idx) => (
                        <li key={idx} className="text-slate-700" style={{ display: 'list-item' }}>
                          <div className="flex items-start gap-2 bg-blue-50 p-2 rounded pr-8">
                            <textarea
                              value={skill}
                              onChange={(e) => {
                                const newSkills = [...editedData.skills.filter(s => s !== '**')];
                                newSkills[idx] = e.target.value;
                                setEditedData({...editedData, skills: newSkills});
                              }}
                              className="flex-1 bg-transparent border-none outline-none focus:ring-0 p-0 resize-none break-words whitespace-pre-wrap min-h-[24px]"
                              placeholder="Enter skill"
                              style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                              onFocus={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                              }}
                              onInput={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                              }}
                            />
                            <button
                              onClick={() => {
                                const newSkills = [...editedData.skills.filter(s => s !== '**')];
                                newSkills.splice(idx, 1);
                                setEditedData({...editedData, skills: newSkills});
                              }}
                              className="text-red-500 hover:text-red-700 text-xs flex-shrink-0 mt-1"
                            >
                              ✕
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button
                    onClick={() => {
                      const newSkills = [...editedData.skills.filter(s => s !== '**'), ''];
                      setEditedData({
                        ...editedData, 
                        skills: newSkills
                      });
                      // Auto-scroll to show new item
                      setTimeout(() => {
                        if (skillsRef.current) {
                          skillsRef.current.scrollTop = skillsRef.current.scrollHeight;
                        }
                      }, 50);
                    }}
                    className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    + Add Skill
                  </button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Preferred Skills</CardTitle>
                </CardHeader>
                <CardContent>
                  <div ref={preferredSkillsRef} className="max-h-64 overflow-y-auto">
                    <ul className="list-disc list-outside space-y-2 pl-6">
                      {editedData.preferred_skills.filter(skill => skill !== '**').map((skill, idx) => (
                        <li key={idx} className="text-slate-700" style={{ display: 'list-item' }}>
                          <div className="flex items-start gap-2 bg-blue-50 p-2 rounded pr-8">
                            <textarea
                              value={skill}
                              onChange={(e) => {
                                const newSkills = [...editedData.preferred_skills.filter(s => s !== '**')];
                                newSkills[idx] = e.target.value;
                                setEditedData({...editedData, preferred_skills: newSkills});
                              }}
                              className="flex-1 bg-transparent border-none outline-none focus:ring-0 p-0 resize-none break-words whitespace-pre-wrap min-h-[24px]"
                              placeholder="Enter preferred skill"
                              style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                              onFocus={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                              }}
                              onInput={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                              }}
                            />
                            <button
                              onClick={() => {
                                const newSkills = [...editedData.preferred_skills.filter(s => s !== '**')];
                                newSkills.splice(idx, 1);
                                setEditedData({...editedData, preferred_skills: newSkills});
                              }}
                              className="text-red-500 hover:text-red-700 text-xs flex-shrink-0 mt-1"
                            >
                              ✕
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button
                    onClick={() => {
                      const newSkills = [...editedData.preferred_skills.filter(s => s !== '**'), ''];
                      setEditedData({
                        ...editedData, 
                        preferred_skills: newSkills
                      });
                      // Auto-scroll to show new item
                      setTimeout(() => {
                        if (preferredSkillsRef.current) {
                          preferredSkillsRef.current.scrollTop = preferredSkillsRef.current.scrollHeight;
                        }
                      }, 50);
                    }}
                    className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    + Add Preferred Skill
                  </button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Experience</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={editedData.experience_years || ''}
                    onChange={(e) => setEditedData({...editedData, experience_years: e.target.value})}
                    placeholder="e.g., 5-7 years"
                    rows={3}
                    className="w-full"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Education</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={editedData.education || ''}
                    onChange={(e) => setEditedData({...editedData, education: e.target.value})}
                    placeholder="e.g., Bachelor's Degree, Master's Degree"
                    rows={2}
                    className="w-full"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Salary Range</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={editedData.salary_range || ''}
                    onChange={(e) => setEditedData({...editedData, salary_range: e.target.value})}
                    placeholder="e.g., $100,000 - $130,000"
                    rows={3}
                    className="w-full"
                  />
                </CardContent>
              </Card>

              {/* Additional Details - Parent Card with nested fields */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Additional Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Travel */}
                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-2">Travel</h3>
                    <Textarea
                      value={editedData.travel || ''}
                      onChange={(e) => setEditedData({...editedData, travel: e.target.value})}
                      placeholder="e.g., No travel, Up to 25%"
                      rows={2}
                      className="w-full"
                    />
                  </div>

                  {/* Relocation */}
                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-2">Relocation</h3>
                    <Textarea
                      value={editedData.relocation_provided || ''}
                      onChange={(e) => setEditedData({...editedData, relocation_provided: e.target.value})}
                      placeholder="e.g., Yes, No, Negotiable"
                      rows={2}
                      className="w-full"
                    />
                  </div>

                  {/* US Citizenship */}
                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-2">US Citizenship</h3>
                    <Textarea
                      value={(editedData.us_citizenship || []).join(', ')}
                      onChange={(e) => {
                        const value = e.target.value.trim();
                        const citizenshipArray = value ? value.split(',').map(v => v.trim()).filter(v => v) : [];
                        setEditedData({...editedData, us_citizenship: citizenshipArray});
                      }}
                      placeholder="e.g., U.S. Citizen, U.S. permanent resident (i.e. Green Card holder)"
                      rows={2}
                      className="w-full"
                    />
                  </div>

                  {/* Security Clearance */}
                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-2">Security Clearance</h3>
                    <Textarea
                      value={editedData.security_clearance || ''}
                      onChange={(e) => setEditedData({...editedData, security_clearance: e.target.value})}
                      placeholder="e.g., None, Confidential, Secret, Top Secret"
                      rows={2}
                      className="w-full"
                    />
                  </div>

                  {/* Job Type */}
                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-2">Job Type</h3>
                    <Textarea
                      value={editedData.job_type || ''}
                      onChange={(e) => setEditedData({...editedData, job_type: e.target.value})}
                      placeholder="e.g., Salaried, Hourly, Part Time, Contract"
                      rows={2}
                      className="w-full"
                    />
                  </div>

                  {/* Shift */}
                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-2">Shift</h3>
                    <Textarea
                      value={editedData.shift_coverage || ''}
                      onChange={(e) => setEditedData({...editedData, shift_coverage: e.target.value})}
                      placeholder="e.g., First Shift (Day), Second Shift (Evening), Third Shift (Night)"
                      rows={2}
                      className="w-full"
                    />
                  </div>

                  {/* Benefits Offered */}
                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-2">Benefits Offered</h3>
                    <Textarea
                      value={(editedData.benefits_offered || []).join(', ')}
                      onChange={(e) => {
                        const value = e.target.value.trim();
                        const benefitsArray = value ? value.split(',').map(v => v.trim()).filter(v => v) : [];
                        setEditedData({...editedData, benefits_offered: benefitsArray});
                      }}
                      placeholder="e.g., Paid time off, Health insurance, 401(k) matching"
                      rows={3}
                      className="w-full"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Location</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">City, State</label>
                    <Input
                      value={editedData.location || ''}
                      onChange={(e) => setEditedData({...editedData, location: e.target.value})}
                      placeholder="e.g., Denver, CO"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Location Benefits</label>
                    <Textarea
                      value={editedData.location_benefits || ''}
                      onChange={(e) => setEditedData({...editedData, location_benefits: e.target.value})}
                      placeholder="Describe location benefits..."
                      rows={4}
                      className="w-full"
                    />
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* View Mode - Matches RequisitionDetail structure */}
              <Card>
                <CardContent className="pt-6 space-y-6">
                  {/* Job Title */}
                  <div>
                    <p className="text-slate-900">
                      <span className="font-bold">Job Title: </span>
                      {requisition.title}
                    </p>
                  </div>

                  {/* Company Brand */}
                  {requisition.company_brand && (
                    <div>
                      <p className="text-slate-900">
                        <span className="font-bold">Company Brand: </span>
                        {requisition.company_brand}
                      </p>
                    </div>
                  )}

                  {/* Description */}
                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-1">Description</h3>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{requisition.description}</p>
                  </div>

                  {/* Key Responsibilities */}
                  {requisition.requirements && requisition.requirements.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-2">Key Responsibilities</h3>
                      <ul className="list-disc list-outside space-y-2 pl-6">
                        {requisition.requirements.filter(req => req && req.trim() && req !== '**').map((req, i) => (
                          <li key={i} className="text-sm text-slate-700">{req}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Required Skills */}
                  {requisition.skills && requisition.skills.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-2">Required Skills</h3>
                      <ul className="list-disc list-outside space-y-2 pl-6">
                        {requisition.skills.filter(skill => skill && skill.trim() && skill !== '**').map((skill, i) => (
                          <li key={i} className="text-sm text-slate-700">{skill}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Preferred Skills */}
                  {requisition.preferred_skills && requisition.preferred_skills.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-2">Preferred Skills</h3>
                      <ul className="list-disc list-outside space-y-2 pl-6">
                        {requisition.preferred_skills.filter(skill => skill && skill.trim() && skill !== '**').map((skill, i) => (
                          <li key={i} className="text-sm text-slate-700">{skill}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Experience - before Education */}
                  {requisition.experience_years && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-1">Experience</h3>
                      <p className="text-sm text-slate-700">{requisition.experience_years}</p>
                    </div>
                  )}

                  {/* Education */}
                  {requisition.education && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-1">Education</h3>
                      <p className="text-sm text-slate-700">{requisition.education}</p>
                    </div>
                  )}

                  {/* Salary Range */}
                  {requisition.salary_range && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-1">Salary Range</h3>
                      <p className="text-sm text-slate-700">{requisition.salary_range}</p>
                    </div>
                  )}

                  {/* Travel */}
                  {requisition.travel && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-1">Travel</h3>
                      <p className="text-sm text-slate-700">{requisition.travel}</p>
                    </div>
                  )}

                  {/* Additional Details Section */}
                  <div>
                    <h3 className="font-semibold text-sm text-slate-600 mb-2">Additional Details</h3>
                    <div className="space-y-1">
                      <p className="text-sm text-slate-700">
                        <span className="font-semibold">Relocation:</span> {requisition.relocation_provided || 'Not specified'}
                      </p>
                      <p className="text-sm text-slate-700">
                        <span className="font-semibold">US Citizenship:</span> {requisition.us_citizenship && requisition.us_citizenship.length > 0 ? requisition.us_citizenship.join(", ") : 'Not specified'}
                      </p>
                      <p className="text-sm text-slate-700">
                        <span className="font-semibold">Security Clearance:</span> {requisition.security_clearance || 'Not specified'}
                      </p>
                      <p className="text-sm text-slate-700">
                        <span className="font-semibold">Job Type:</span> {requisition.job_type || 'Not specified'}
                      </p>
                      <p className="text-sm text-slate-700">
                        <span className="font-semibold">Shift:</span> {requisition.shift_coverage || 'Not specified'}
                      </p>
                      <p className="text-sm text-slate-700">
                        <span className="font-semibold">Benefits Offered:</span> {requisition.benefits_offered && requisition.benefits_offered.length > 0 ? requisition.benefits_offered.join(", ") : 'Not specified'}
                      </p>
                    </div>
                  </div>

                  {/* Location */}
                  {(requisition.location || requisition.location_benefits) && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-1">Location</h3>
                      {requisition.location && (
                        <p className="text-sm text-slate-700 mb-1">{requisition.location}</p>
                      )}
                      {requisition.location_benefits && (
                        <p className="text-sm text-slate-700">{requisition.location_benefits}</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
